// @flow

/**
 * Virtualizer is a component that accepts a layout and screen bounds and
 * renders only the items in that layout that lie within the bounds. This allows
 * us to render as little content as possible into the DOM.
 *
 * Note: By virtualizing items in the grid, local state may be destroyed as the
 * user scrolls. Any time a grid item needs to persist chances, those changes
 * must be persisted in some external store (ex: Redux).
 */

import * as React from 'react';
import Layout, { type Size, type Position } from 'app/common/react/components/Masonry9/Layout';

// Control how much space around the viewport we populate with grid reps.
const DEFAULT_OVERSCAN = 200;

type Props<ItemType> = {|
  scrollContainerSize: Size,
  scrollPosition: Position,
  layout: Layout<ItemType>,
  overscan: number,
  children: (layout: Layout<ItemType>, fullLayoutSize?: Size) => React.Node,
|};

function Virtualizer<ItemType>({
  layout,
  overscan = DEFAULT_OVERSCAN,
  scrollContainerSize,
  scrollPosition,
  children,
}: Props<ItemType>) {
  // Calculate the visible bounds.
  const visibleOrigin: Position = {
    top: Math.max(0, scrollPosition.top),
    left: Math.max(0, scrollPosition.left),
  };

  const visibleSize: Size = {
    width: Math.min(
      Math.max(0, scrollContainerSize.width + scrollPosition.left),
      scrollContainerSize.width,
    ),
    height: Math.min(
      Math.max(0, scrollContainerSize.height + scrollPosition.top),
      scrollContainerSize.height,
    ),
  };

  // Figure out the bounds of the layout we must populate. If there's an
  // overscan (and a roundOverscanToNearestInt by proxy), round the cutoff
  // points to the nearest roundOverscanToNearestInt.
  const minTop = visibleOrigin.top - overscan;
  const maxBottom = visibleOrigin.top + visibleSize.height + overscan;
  const minLeft = visibleOrigin.left - overscan;
  const maxRight = visibleOrigin.left + visibleSize.width + overscan;

  // Iterate over each item in our layout and check to see if its size and
  // position cause it to land inside the virtual bounds.
  const itemsToInclude: Array<ItemType> = layout.items.filter((item) => {
    const itemLayoutInfo = layout.getItemLayoutInfo(item);

    // Ignore items with no layout info.
    if (!itemLayoutInfo) {
      return false;
    }

    const { size, position } = itemLayoutInfo;

    // If an item has a size/position, we can check its bounds. Otherwise,
    // it's a rep that needs measuring and should be rendered.
    if (size && position) {
      if (
        position.top + size.height >= minTop &&
        position.top <= maxBottom &&
        position.left + size.width >= minLeft &&
        position.left <= maxRight
      ) {
        return true;
      }
    } else {
      return true;
    }

    return false;
  });

  // Clone the layout, but only use the reduced set of items.
  const virtualLayout = new Layout(itemsToInclude, layout.getItemLayoutInfo);

  // The cloned layout should have all the same properties.
  virtualLayout.size = layout.size;
  virtualLayout.contentFilledSize = layout.contentFilledSize;
  virtualLayout.isMeasuring = layout.isMeasuring;

  // Render the new layout along with full-size layout's size.
  return children(virtualLayout, layout.size ? layout.size : undefined);
}

export default Virtualizer;
